/**
 * EInvoiceProposalRequest — the Jäsenmaksu e-laskuehdotus surface (048).
 *
 * Replaces `EInvoiceOrder.tsx`, which hardcoded nine Finnish bank NAMES, sent
 * `{ bank: '<name>' }`, and — the reason it could not survive — showed its
 * confirmation *regardless of the outcome*. A member whose request failed was
 * told it had succeeded (the prototype does the same). FR-008/SC-002 exist to
 * stop exactly that.
 *
 * Its own component rather than more of `MembershipFeeSection`: that file is
 * already 200+ lines and the web 400-line rule is enforced by an architecture
 * test. For the same reason the bank-picker form lives in
 * `EInvoiceBankPicker.tsx` — this file keeps the state machine (the submit
 * intent, its idempotency key, the outcome branches), that one is presentation.
 *
 * LAYOUT follows the prototype: the delivery method is the LATEST invoice
 * card's last row (`{/* Laskutustapa - only for first/latest invoice *\/}`) with
 * the switch affordance inline on the right, and the request form opens BELOW
 * the card. The card renders the row (it owns that chrome) and this component
 * owns the affordance, the form and every outcome — hence the two slots. The
 * proto's Radix DIALOG is deliberately NOT adopted: D-127 (inline / no modal,
 * as amended by 048) keeps this flow INLINE (`MembershipFeeSection.protoParity`
 * KEEP-guard; the divergences are recorded as D-PD-008).
 *
 * What it renders is the SERVER's eligibility derivation, never its own. The
 * same derivation enforces the write, so a second client-side copy could
 * disagree with the one that actually decides — and the disagreement would show
 * up as an action that looks available and always fails.
 *
 * Owned by feature 048-member-einvoice-request.
 * Task: T017
 */

import { type JSX, type RefObject, useEffect, useId, useRef, useState } from 'react';

import { useTranslate } from '../../i18n/useTranslate.js';
import type {
  BillingFacts,
  EInvoiceRequestOutcome,
  EInvoiceProposalRequest as ProposalRequest,
} from '../../membership-billing/MembershipBillingPort.js';
import { EInvoiceBankPicker } from './EInvoiceBankPicker.js';
import { METHOD_LABEL_KEYS, mintIdempotencyKey, paragraphs } from './einvoiceProposalHelpers.js';
import {
  hidesAction,
  isSupersededBy,
  type Submission,
  submissionMessageKey,
} from './einvoiceSubmission.js';
import { formatIsoDate } from './formatIsoDate.js';
import { useReReadFocus } from './useReReadFocus.js';

export interface EInvoiceProposalRequestProps {
  readonly facts: BillingFacts;
  /**
   * Render the delivery-method row inside the latest invoice card (the proto's
   * placement). Given the method label and the inline affordance, it returns
   * whatever the caller wants to do with them.
   */
  readonly children: (slots: {
    readonly methodLabel: string;
    readonly action: JSX.Element | null;
    /** Attach to the method value: the focus home for `already-einvoice`/`method-unknown`. */
    readonly methodRef: RefObject<HTMLSpanElement | null>;
  }) => JSX.Element;
  readonly onRequest: (
    request: ProposalRequest,
    idempotencyKey: string,
  ) => EInvoiceRequestOutcome | PromiseLike<EInvoiceRequestOutcome>;
  /** Re-read the billing facts after every settled attempt (contract §4). */
  readonly onSettled: () => void;
  readonly locale: string;
}

export function EInvoiceProposalRequest({
  facts,
  children,
  onRequest,
  onSettled,
  locale,
}: EInvoiceProposalRequestProps): JSX.Element {
  const t = useTranslate();
  const selectId = useId();
  const bankRequiredId = useId();
  const formId = useId();
  const statusMessageId = useId();
  const pendingHeadingId = useId();
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const [showBankRequired, setShowBankRequired] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [submission, setSubmission] = useState<Submission>({ kind: 'idle' });
  // One key per BANK for this component's life (an intent is "this bank"), so
  // re-picking a bank (A→B→A) reuses its key and a retry replays upstream rather
  // than risking a second proposal. Cleared on success.
  const intentKeys = useRef(new Map<string, string>());
  // Focus never drops to <body>, and moves only AFTER the render that needs it:
  // an outcome → the message (as MembershipFeeSection's pay block does); a
  // missing bank → the picker, once its error is wired to it (else unannounced);
  // Cancel → the toggle, since Cancel unmounts the button that held focus.
  const statusRef = useRef<HTMLDivElement | null>(null);
  // Focus lands on the re-read's own statement instead once it supersedes the
  // outcome, or the picker unmounts (see `useReReadFocus`).
  const pendingRef = useRef<HTMLElement | null>(null);
  const unavailableRef = useRef<HTMLParagraphElement | null>(null);
  const methodRef = useRef<HTMLSpanElement | null>(null);
  const [focusTarget, setFocusTarget] = useState<'status' | 'select' | 'toggle' | null>(null);
  useEffect(() => {
    if (focusTarget === null) return;
    setFocusTarget(null);
    ({ status: statusRef, select: selectRef, toggle: toggleRef })[focusTarget].current?.focus();
  }, [focusTarget]);

  const { billingMethod, eligibility } = facts;
  // A re-read (e.g. after `unsupported-bank`) may drop the selected bank: never
  // keep a selection the fresh list no longer offers.
  const bankIds = eligibility.kind === 'can-request' ? eligibility.banks.map((b) => b.id) : [];
  const bankKey = bankIds.join(',');
  useEffect(() => {
    if (selectedBankId !== '' && !bankKey.split(',').includes(selectedBankId))
      setSelectedBankId('');
  }, [bankKey, selectedBankId]);
  const sending = submission.kind === 'sending';

  // Once the re-read lands, the view it renders is the authoritative answer:
  // the transient outcome says nothing next to it (`isSupersededBy`; e.g. the
  // confirmation beside the pending block read as two stacked success
  // messages). The confirmation still covers the window between submit and the
  // re-read. Its one unique detail (1–3 arkipäivää to arrive) is NOT in the
  // pending copy; moving it is a copy change for the owner (copy-fi.md §4).
  const superseded = isSupersededBy(submission, eligibility.kind);
  // A live region that is always mounted: one mounted together with its text
  // announces unreliably.
  const messageKey = submissionMessageKey(submission, superseded);
  const liveMessage = messageKey === null ? '' : t(messageKey);
  useReReadFocus({
    eligibilityKind: eligibility.kind,
    superseded,
    liveMessage,
    pickerOpen: isOpen,
    closePicker: () => setIsOpen(false),
    refs: {
      status: statusRef,
      pending: pendingRef,
      unavailable: unavailableRef,
      method: methodRef,
    },
  });

  // Rendered in EVERY branch: the re-read after an attempt usually moves the
  // eligibility (e.g. to `pending` after a success), and a live region that
  // unmounts with it would take the outcome message — often unannounced — with
  // it. The focus target wraps the region rather than sitting inside it, so
  // focusing it does not truncate the announcement.
  const status = (
    // A stable KEY keeps this the same DOM node when the re-read moves the
    // eligibility to another branch (e.g. `pending`), where it sits at a
    // different index: remounting it would drop the focus it holds to <body>
    // and recreate the live region with its text already inside.
    <div
      key="einvoice-status"
      ref={statusRef}
      tabIndex={-1}
      className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div
        className={
          liveMessage === '' ? 'sr-only' : 'rounded-lg bg-muted p-3 text-sm text-muted-foreground'
        }
        id={statusMessageId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveMessage}
      </div>
    </div>
  );

  const methodLabel = t(METHOD_LABEL_KEYS[billingMethod]);

  // A member already on e-invoicing needs no prompt and no action (SC-004);
  // the row alone is the honest, complete answer. Same for a method we could
  // not determine (FR-001a): state it plainly and offer NOTHING — offering the
  // switch to someone already on e-invoice is the exact harm `unknown` exists
  // to prevent.
  if (eligibility.kind === 'already-einvoice' || eligibility.kind === 'method-unknown') {
    return (
      <>
        {children({ methodLabel, action: null, methodRef })}
        {status}
      </>
    );
  }

  if (eligibility.kind === 'unavailable') {
    return (
      <>
        {children({ methodLabel, action: null, methodRef })}
        <p
          ref={unavailableRef}
          tabIndex={-1}
          className="text-sm text-muted-foreground focus-visible:rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {t('profile.fee.eInvoice.unavailable')}
        </p>
        {status}
      </>
    );
  }

  if (eligibility.kind === 'pending') {
    const [pendingHeading, ...pendingBody] = paragraphs(
      t('profile.fee.eInvoice.pending')
        .replace('{date}', formatIsoDate(eligibility.requestDate, locale))
        .replace('{again}', formatIsoDate(eligibility.canRequestAgainOn, locale)),
    );
    return (
      <>
        {children({ methodLabel, action: null, methodRef })}
        <section
          ref={pendingRef}
          tabIndex={-1}
          aria-labelledby={pendingHeadingId}
          aria-describedby={pendingBody.map((_, i) => pendingHeadingId + i).join(' ')}
          className="flex flex-col gap-1 text-sm text-muted-foreground focus-visible:rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <h4 id={pendingHeadingId} className="text-sm font-semibold text-foreground">
            {pendingHeading}
          </h4>
          {pendingBody.map((text, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a static split of one copy string
            <p key={index} id={pendingHeadingId + index}>
              {text}
            </p>
          ))}
        </section>
        {status}
      </>
    );
  }

  const submit = (): void => {
    const bankId = Number(selectedBankId);
    if (submission.kind === 'sending') return;
    if (selectedBankId === '' || !Number.isInteger(bankId)) {
      // Say why nothing happened, next to the control that fixes it.
      setShowBankRequired(true);
      setFocusTarget('select');
      return;
    }

    const idempotencyKey = intentKeys.current.get(selectedBankId) ?? mintIdempotencyKey();
    intentKeys.current.set(selectedBankId, idempotencyKey);

    setSubmission({ kind: 'sending' });
    // Set ONLY by the session-expired branch. A re-read needs the same session
    // the request just failed on, so it fails too — and the section's error
    // boundary then swaps the whole surface for the generic "unavailable"
    // fallback, replacing the "sign in again" message with the one piece of
    // advice that cannot work here, and dropping focus with it.
    let skipSettle = false;
    // `Promise.resolve().then(...)` rather than `Promise.resolve(onRequest(...))`:
    // a port that throws SYNCHRONOUSLY would otherwise escape this chain
    // entirely, leaving the button stuck on 'sending' with no message — and
    // the `.catch` below unreached.
    void Promise.resolve()
      .then(() => onRequest({ bankId }, idempotencyKey))
      .then((outcome) => {
        if (outcome.status === 'ok') {
          setSubmission({ kind: 'sent' });
          setIsOpen(false);
          setSelectedBankId('');
          setFocusTarget('status');
          // The intent completed: the next request is a NEW intent.
          intentKeys.current.clear();
          return;
        }
        if (outcome.status === 'unconfirmed') {
          // KEEP the key (contract §3b): the request may have been accepted, so
          // a retry of this intent must replay it upstream, not send another.
          setSubmission({ kind: 'unconfirmed' });
          setIsOpen(false);
          setFocusTarget('status');
          return;
        }
        if (outcome.status === 'session-expired') {
          // Nothing reached the register, and no retry can work until they sign
          // in again — so this must NOT read as "try again shortly".
          //
          // Nothing was written either, so there is no upstream state to
          // re-read: the settle exists to reveal a change this outcome cannot
          // have caused. Skipping it is what KEEPS the message on screen.
          skipSettle = true;
          setSubmission({ kind: 'session-expired' });
          setIsOpen(false);
          setFocusTarget('status');
          return;
        }
        // Keep the key: a retry of the SAME intent should replay upstream, not
        // create a second proposal. Keep the picker open for a bank the member
        // can change (a new bank mints a new key); close it otherwise.
        setSubmission({ kind: 'refused', reason: outcome.reason });
        if (outcome.reason !== 'unsupported-bank') {
          setIsOpen(false);
          setFocusTarget('status');
        }
      })
      .catch(() => {
        // A transport-level throw is not a success. Never fall through to the
        // confirmation — the failure the retired component had.
        setSubmission({ kind: 'refused', reason: 'unavailable' });
        setIsOpen(false);
        setFocusTarget('status');
      })
      .finally(() => {
        // Re-read after every outcome that could have changed something
        // upstream: after an undetermined one the authoritative state is there,
        // and this is how the member sees the truth. The single exception is an
        // expired session, where the re-read cannot succeed and would destroy
        // the only message that helps (see `skipSettle`).
        if (skipSettle) return;
        onSettled();
      });
  };

  const hideAction = hidesAction(submission);
  // A refused bank stays selected in the still-open picker: tie the refusal to it.
  const bankRefused = submission.kind === 'refused' && submission.reason === 'unsupported-bank';
  const selectHint = showBankRequired ? bankRequiredId : bankRefused ? statusMessageId : undefined;
  const action = hideAction ? null : (
    <button
      ref={toggleRef}
      type="button"
      onClick={() => {
        // Nothing may reset or reopen the flow while a request is in flight.
        if (sending) return;
        setIsOpen((open) => !open);
        setShowBankRequired(false);
        setSubmission({ kind: 'idle' });
      }}
      aria-expanded={isOpen}
      aria-controls={isOpen ? formId : undefined}
      aria-disabled={sending}
      className="text-sm font-semibold text-primary transition-colors hover:text-primary/80 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
    >
      {t('profile.fee.eInvoice.request')}
    </button>
  );

  return (
    <>
      {children({ methodLabel, action, methodRef })}

      {/* Switch guidance for a member on paper (copy §2, FR-002) — not while the
          register says a proposal is already pending, which the action hides too. */}
      {billingMethod === 'paper' && !isOpen && !hideAction && (
        <p className="text-sm text-muted-foreground">{t('profile.fee.eInvoice.guidance')}</p>
      )}

      {isOpen && (
        <EInvoiceBankPicker
          formId={formId}
          selectId={selectId}
          bankRequiredId={bankRequiredId}
          selectRef={selectRef}
          banks={eligibility.banks}
          selectedBankId={selectedBankId}
          sending={sending}
          showBankRequired={showBankRequired}
          bankRefused={bankRefused}
          describedBy={selectHint}
          onSelect={(bankId) => {
            setSelectedBankId(bankId);
            setShowBankRequired(false);
            // The key follows the bank at submit time (see `intentKeys`).
            setSubmission({ kind: 'idle' });
          }}
          onCancel={() => {
            setIsOpen(false);
            setSelectedBankId('');
            setShowBankRequired(false);
            setSubmission({ kind: 'idle' });
            setFocusTarget('toggle');
          }}
          onSubmit={submit}
        />
      )}

      {status}
    </>
  );
}
